import { useEffect, useMemo, useRef, useState } from "react";
import { Search, Package, MapPin, Copy, Download, Filter } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const fmt = (n) => new Intl.NumberFormat("es-DO").format(Math.round(n || 0));
const PAGE_SIZE = 50;

/**
 * Inventory explorer / query builder — lives at the bottom of the Forecast
 * dashboard. Lets managers slice the inventory by Unidad de Negocio (almacén),
 * Marca, Modelo and Ubicación, then export/copy the resulting VIN list.
 *
 * Keeps its own state (filters, page) so it doesn't disturb the forecast
 * upstream. Only mounts filters after ``/predictive/inventory-options``
 * resolves so users see real dropdown values, not "Todos" alone.
 */
export default function InventorySearchSection() {
  const [opts, setOpts] = useState({ brands: [], models: [], almacenes: [], ubicaciones: [] });
  const [filters, setFilters] = useState({
    brand: "Todas", model: "Todos", branch: "Todas", ubicacion: "Todas", only_available: true,
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const abortRef = useRef(null);

  useEffect(() => {
    api.get("/predictive/inventory-options").then((r) => setOpts(r.data)).catch(() => {});
  }, []);

  const buildParams = (o = offset) => ({
    ...(filters.brand !== "Todas" && { brand: filters.brand }),
    ...(filters.model !== "Todos" && { model: filters.model }),
    ...(filters.branch !== "Todas" && { branch: filters.branch }),
    ...(filters.ubicacion !== "Todas" && { ubicacion: filters.ubicacion }),
    only_available: filters.only_available,
    limit: PAGE_SIZE,
    offset: o,
  });

  const fetchPage = (o = 0) => {
    setLoading(true);
    setOffset(o);
    if (abortRef.current) abortRef.current.abort();
    const c = new AbortController();
    abortRef.current = c;
    const qs = new URLSearchParams(buildParams(o)).toString();
    api.get(`/predictive/inventory-search?${qs}`, { signal: c.signal })
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchPage(0); /* eslint-disable-next-line */ }, [filters]);

  const totalPages = data ? Math.ceil((data.total || 0) / PAGE_SIZE) : 0;
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

  const copyVins = async () => {
    if (!data || !data.items.length) { toast.warning("No hay resultados para copiar"); return; }
    const text = data.items.map((s) => s.chasis).filter(Boolean).join("\n");
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${data.items.length} VINs copiados (página actual)`);
    } catch {
      toast.error("No se pudo copiar. Selecciona manualmente.");
    }
  };

  const exportCsv = () => {
    if (!data || !data.items.length) return;
    const esc = (v) => {
      if (v == null) return "";
      const s = String(v).replace(/"/g, '""');
      return /[",\n;]/.test(s) ? `"${s}"` : s;
    };
    const lines = [];
    lines.push(`Consulta de inventario;Generado;${new Date().toISOString()}`);
    Object.entries(filters).forEach(([k, v]) => { lines.push(`${k};${v}`); });
    lines.push(`Total resultados;${data.total}`);
    lines.push("");
    lines.push(["Chasis","Marca","Modelo","Descripción","Año","Color","Estado","Almacén","Ubicación","Días en inv","Antigüedad"].map(esc).join(";"));
    data.items.forEach((s) => {
      lines.push([s.chasis, s.brand, s.model, s.descripcion, s.model_year, s.color_ext,
                  s.estado, s.desc_almacen, s.ubicacion, s.dias_en_inv, s.antiguedad].map(esc).join(";"));
    });
    const csv = "\uFEFF" + lines.join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `inventario_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const setF = (k, v) => setFilters((p) => ({ ...p, [k]: v }));
  const reset = () => setFilters({ brand: "Todas", model: "Todos", branch: "Todas",
                                    ubicacion: "Todas", only_available: true });

  return (
    <details className="surface p-6 mb-12" data-testid="inventory-search-section">
      <summary className="cursor-pointer list-none">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Search size={18} style={{ color: "#3b82f6" }}/>
            <h2 className="text-lg font-extrabold font-display">Consulta de Inventario</h2>
            <span className="text-[10px] font-extrabold uppercase tracking-wider px-2 py-0.5 rounded-full"
                  style={{ background: "#eff6ff", color: "#1d4ed8" }}>
              Click para expandir
            </span>
          </div>
          <span className="text-xs text-gray-500 font-semibold">
            Filtrado avanzado por Unidad de Negocio · Marca · Modelo · Ubicación
          </span>
        </div>
        <p className="text-xs text-gray-500 font-semibold mt-2">
          Ordenado por antigüedad (chasis más viejos primero) para priorizar rotación.
        </p>
      </summary>

      <div className="mt-5">

      {/* Filters */}
      <div className="grid gap-3 mb-4" style={{ gridTemplateColumns: "repeat(4, minmax(0, 1fr))" }}>
        <SearchDropdown label="Unidad de Negocio" value={filters.branch} sentinel="Todas"
          options={opts.almacenes} onChange={(v) => setF("branch", v)} testid="inv-branch"/>
        <SearchDropdown label="Marca" value={filters.brand} sentinel="Todas"
          options={opts.brands} onChange={(v) => setF("brand", v)} testid="inv-brand"/>
        <SearchDropdown label="Modelo" value={filters.model} sentinel="Todos"
          options={opts.models} onChange={(v) => setF("model", v)} testid="inv-model"/>
        <SearchDropdown label="Ubicación" value={filters.ubicacion} sentinel="Todas"
          options={opts.ubicaciones} onChange={(v) => setF("ubicacion", v)} testid="inv-ubicacion"/>
      </div>

      <div className="flex items-center gap-3 flex-wrap mb-4">
        <label className="flex items-center gap-2 text-xs font-extrabold cursor-pointer"
               style={{ color: "#111827" }} data-testid="inv-only-available">
          <input type="checkbox" checked={filters.only_available}
                 onChange={(e) => setF("only_available", e.target.checked)}
                 className="accent-amber-500"/>
          Solo unidades disponibles
        </label>
        <button onClick={reset}
                className="text-xs font-extrabold text-gray-500 hover:text-gray-900 flex items-center gap-1"
                data-testid="inv-reset"><Filter size={12}/> Borrar Filtros</button>
        <div className="ml-auto flex items-center gap-2">
          {data && data.items.length > 0 && (
            <>
              <button onClick={copyVins}
                      className="px-3 py-1.5 rounded-lg font-extrabold text-xs flex items-center gap-1.5 border border-gray-200 hover:bg-gray-50"
                      data-testid="inv-copy-vins">
                <Copy size={12}/> Copiar VINs
              </button>
              <button onClick={exportCsv}
                      className="px-3 py-1.5 rounded-lg font-extrabold text-xs flex items-center gap-1.5 border border-gray-200 hover:bg-gray-50"
                      data-testid="inv-export-csv">
                <Download size={12}/> Exportar CSV
              </button>
            </>
          )}
        </div>
      </div>

      {/* Aggregate strip: totals + top brand + top warehouse */}
      {data && (
        <div className="grid grid-cols-3 gap-3 mb-4 p-3 rounded-lg" style={{ background: "#f8fafc" }}>
          <div>
            <div className="text-[10px] font-extrabold uppercase tracking-wider text-gray-500">Total</div>
            <div className="font-display font-extrabold text-2xl">{fmt(data.total)}</div>
          </div>
          <TopBucket label="Distribución por marca" entries={data.by_brand}/>
          <TopBucket label="Distribución por almacén" entries={data.by_almacen}/>
        </div>
      )}

      {/* Results table */}
      {loading && <div className="p-6 text-center text-sm font-semibold text-gray-500">Cargando…</div>}
      {!loading && data && data.items.length === 0 && (
        <div className="p-6 text-center text-sm font-semibold text-gray-400">
          No hay resultados con los filtros seleccionados.
        </div>
      )}
      {!loading && data && data.items.length > 0 && (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="inv-results-table">
              <thead>
                <tr className="text-left text-[10px] font-extrabold uppercase tracking-wider text-gray-500 border-b border-gray-200">
                  <th className="px-2 py-2">Chasis</th>
                  <th className="px-2 py-2">Marca · Modelo</th>
                  <th className="px-2 py-2">Año · Color</th>
                  <th className="px-2 py-2">Almacén</th>
                  <th className="px-2 py-2">Ubicación</th>
                  <th className="px-2 py-2">Estado</th>
                  <th className="px-2 py-2 text-right">Días</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((r) => {
                  const days = r.dias_en_inv || 0;
                  const daysColor = days > 180 ? "#b45309" : days > 60 ? "#d97706" : "#16a34a";
                  return (
                    <tr key={r.chasis} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="px-2 py-1.5 font-mono font-extrabold text-[11px]" title={r.chasis}>{r.chasis}</td>
                      <td className="px-2 py-1.5 font-semibold">{r.brand} · {r.model}</td>
                      <td className="px-2 py-1.5 text-gray-600">{r.model_year || "—"} {r.color_ext || ""}</td>
                      <td className="px-2 py-1.5 text-gray-600 truncate max-w-[180px]" title={r.desc_almacen}>
                        <MapPin size={10} className="inline mr-1 text-gray-400"/>{r.desc_almacen || "—"}
                      </td>
                      <td className="px-2 py-1.5 text-gray-500 text-[11px]">{r.ubicacion || "—"}</td>
                      <td className="px-2 py-1.5">
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-extrabold"
                              style={{ background: r.available ? "#dcfce7" : "#fef3c7",
                                       color: r.available ? "#166534" : "#92400e" }}>
                          {r.available ? "DISPONIBLE" : (r.estado || "OCUPADO").toUpperCase().slice(0, 18)}
                        </span>
                      </td>
                      <td className="px-2 py-1.5 text-right font-extrabold" style={{ color: daysColor }}>
                        {days}d
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
              <div className="text-xs font-bold text-gray-500">
                Página {currentPage} de {totalPages} · {fmt(data.total)} resultados totales
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => fetchPage(Math.max(0, offset - PAGE_SIZE))}
                        disabled={offset === 0}
                        className="px-3 py-1 rounded font-extrabold text-xs border border-gray-200 hover:bg-gray-50 disabled:opacity-40"
                        data-testid="inv-page-prev">← Anterior</button>
                <button onClick={() => fetchPage(offset + PAGE_SIZE)}
                        disabled={offset + PAGE_SIZE >= data.total}
                        className="px-3 py-1 rounded font-extrabold text-xs border border-gray-200 hover:bg-gray-50 disabled:opacity-40"
                        data-testid="inv-page-next">Siguiente →</button>
              </div>
            </div>
          )}
        </>
      )}
      </div>
    </details>
  );
}

function TopBucket({ label, entries }) {
  const items = useMemo(() => Object.entries(entries || {}).slice(0, 4), [entries]);
  const total = useMemo(() => Object.values(entries || {}).reduce((s, n) => s + n, 0), [entries]);
  return (
    <div>
      <div className="text-[10px] font-extrabold uppercase tracking-wider text-gray-500 mb-1">{label}</div>
      {items.length === 0 ? (
        <span className="text-xs font-semibold text-gray-400">—</span>
      ) : (
        <div className="space-y-0.5">
          {items.map(([k, v]) => (
            <div key={k} className="flex items-center gap-2 text-[11px]">
              <span className="font-bold text-gray-700 truncate flex-1" title={k}>{k}</span>
              <span className="font-extrabold text-gray-900 flex-shrink-0" style={{ fontVariantNumeric: "tabular-nums" }}>
                {fmt(v)}
                <span className="text-gray-400 ml-1">
                  ({total ? Math.round((v / total) * 100) : 0}%)
                </span>
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Small filter dropdown with client-side text search — no external dep.
 */
function SearchDropdown({ label, value, sentinel, options, onChange, testid }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return options.slice(0, 200);
    return options.filter((o) => String(o).toLowerCase().includes(s)).slice(0, 200);
  }, [q, options]);
  return (
    <div ref={ref} className="relative" data-testid={testid}>
      <label className="text-[10px] font-extrabold uppercase tracking-wider text-gray-500 block mb-1">{label}</label>
      <button type="button" onClick={() => setOpen((v) => !v)}
              className="w-full text-left px-3 py-2 rounded-lg border border-gray-200 text-sm font-semibold bg-white hover:border-gray-300 truncate"
              data-testid={`${testid}-trigger`}>
        {value}
      </button>
      {open && (
        <div className="absolute z-30 top-full left-0 right-0 mt-1 rounded-lg border border-gray-200 bg-white shadow-lg max-h-72 overflow-hidden flex flex-col">
          <input type="text" value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Buscar…"
                 autoFocus
                 className="px-3 py-2 border-b border-gray-100 text-xs font-semibold focus:outline-none"/>
          <div className="overflow-y-auto flex-1">
            <button type="button" onClick={() => { onChange(sentinel); setOpen(false); setQ(""); }}
                    className="w-full text-left px-3 py-1.5 text-xs font-extrabold hover:bg-amber-50 truncate">
              {sentinel}
            </button>
            {filtered.map((o) => (
              <button type="button" key={o}
                      onClick={() => { onChange(o); setOpen(false); setQ(""); }}
                      className={`w-full text-left px-3 py-1.5 text-xs font-semibold truncate ${o === value ? "bg-amber-50" : "hover:bg-gray-50"}`}>
                {o}
              </button>
            ))}
            {filtered.length === 0 && (
              <div className="px-3 py-2 text-xs font-semibold text-gray-400">Sin coincidencias</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
